import { type FC, memo } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { dialogHeader } from "@stubhub/broadway-foundation/recipes";
import { Button, ButtonKind, ButtonSize, ButtonVariant, Modal, ModalBody, ModalHeader } from "@stubhub/broadway-web";
import { ArtistIcon, ShareIcon } from "@stubhub/broadway-web/icons/functional";
import { Content, FormatContent } from "src/shared/components/content";
import { ListFavButton } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/ListFavButton";
import {
  drawerButton,
  drawerButtonsContainer,
} from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/styles";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { SHARING_ORIGINS } from "src/website-apps/shared/components/stubhub-sharing/SharingOrigins";
import { useShareData } from "src/website-apps/shared/components/stubhub-sharing/utils/useShareData";

// Well past Broadway's own 700ms close-transition fallback timer, so this only ever
// kicks in if Broadway stops signalling the unlock via `body.style.position`.
const FALLBACK_UNLOCK_TIMEOUT_MS = 1000;

/**
 * Broadway's Modal locks body scroll (`position: fixed`) while open, and only restores it -
 * including its own `window.scrollTo` back to the pre-open position - after the close
 * transition finishes. Calling `scrollToElement` before that restore runs either no-ops
 * (scroll is still locked) or gets immediately overwritten by it, so wait for the actual
 * DOM mutation that clears the lock rather than guessing at Broadway's transition timing.
 */
const scrollAfterModalCloses = (scrollToElement: () => void) => {
  if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
    scrollToElement();
    return;
  }

  const isScrollLocked = () => document.body.style.position === "fixed";
  if (!isScrollLocked()) {
    scrollToElement();
    return;
  }

  const cleanup: { observer?: MutationObserver; timer?: ReturnType<typeof setTimeout> } = {};

  const settle = () => {
    cleanup.observer?.disconnect();
    clearTimeout(cleanup.timer);
    scrollToElement();
  };

  cleanup.observer = new MutationObserver(() => {
    if (!isScrollLocked()) settle();
  });
  cleanup.observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
  cleanup.timer = setTimeout(settle, FALLBACK_UNLOCK_TIMEOUT_MS);
};

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  categoryId?: number | undefined;
  scrollToPerformer: () => void;
  categoryName: string;
  categoryImageUrl?: string | null | undefined;
  catHasPerformerCard: boolean;
}

/**
 * Mobile drawer component that displays overflow header buttons
 * for performer banner actions (About, Add to Favorites, Share)
 */
export const MobileBannerOverflowDrawer: FC<Props> = memo(
  ({ isOpen, handleClose, categoryId, scrollToPerformer, categoryName, categoryImageUrl, catHasPerformerCard }) => {
    const { onShare, shareState } = useShareData(SHARING_ORIGINS.PERFORMER, { title: categoryName });
    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <ModalHeader
          classes={{
            ...dialogHeader(),
            wrapper: css({
              height: "primitives.600",
              paddingBlockStart: "primitives.0",
              paddingBlockEnd: "primitives.0",
            }),
          }}
        />
        <ModalBody>
          <div className={drawerButtonsContainer}>
            <ListFavButton
              entityId={categoryId ?? -1}
              entityType={ENTITY_TYPES.CATEGORY}
              onAfterClick={handleClose}
              previewSummary={{ name: categoryName, imageUrl: categoryImageUrl ?? null }}
            />

            {shareState === "supported" && (
              <Button
                classes={{ root: drawerButton }}
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Default}
                kind={ButtonKind.Neutral}
                startIcon={ShareIcon}
                onClick={() => {
                  onShare();
                  handleClose();
                }}
              >
                {/* "Share" */}
                <Content id="cid$#3929949#" />
              </Button>
            )}

            {catHasPerformerCard && (
              <Button
                classes={{ root: drawerButton }}
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Default}
                kind={ButtonKind.Neutral}
                startIcon={ArtistIcon}
                onClick={() => {
                  scrollAfterModalCloses(scrollToPerformer);
                  handleClose();
                }}
              >
                {/* "About {0}" */}
                <FormatContent id="cid$#3928806#" params={categoryName} />
              </Button>
            )}
          </div>
        </ModalBody>
      </Modal>
    );
  },
);

MobileBannerOverflowDrawer.displayName = "MobileBannerOverflowDrawer";
